import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, HostListener, OnDestroy, Renderer2, inject } from '@angular/core';

@Component({
  selector: 'lib-app-shell-layout',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="app-shell"
      [class.app-shell--mobile-open]="isMobileSidebarOpen"
      (touchstart)="onShellTouchStart($event)"
      (touchend)="onShellTouchEnd($event)"
    >
      <div
        class="app-shell__overlay"
        (click)="closeMobileSidebar()"
        [attr.aria-hidden]="!isMobileSidebarOpen"
      ></div>

      <aside
        id="app-shell-sidebar"
        class="app-shell__sidebar"
        [attr.aria-hidden]="isMobileSidebarOpen ? null : 'true'"
        (click)="onSidebarInteraction($event)"
        (touchstart)="onSidebarTouchStart($event)"
        (touchend)="onSidebarTouchEnd($event)"
      >
        <ng-content select="[appShellSidebar]"></ng-content>
      </aside>

      <div class="app-shell__main">
        <header class="app-shell__topbar">
          <button
            type="button"
            class="app-shell__menu-toggle"
            (click)="toggleMobileSidebar()"
            aria-label="Toggle navigation menu"
            aria-controls="app-shell-sidebar"
            [attr.aria-expanded]="isMobileSidebarOpen"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <div class="app-shell__topbar-content">
            <ng-content select="[appShellTopbar]"></ng-content>
          </div>
        </header>

        <main class="app-shell__content">
          <ng-content></ng-content>
        </main>
      </div>
    </div>
  `,
  styles: [`
    .app-shell {
      position: relative;
      min-height: 100vh;
      display: flex;
      background:
        radial-gradient(circle at 0% 0%, color-mix(in srgb, var(--tenant-primary, #2563eb) 18%, transparent), transparent 35%),
        radial-gradient(circle at 100% 100%, color-mix(in srgb, var(--tenant-accent, #f59e0b) 18%, transparent), transparent 45%),
        #f8fafc;
    }

      .app-shell__overlay {
        display: none;
        opacity: 0;
      }

    .app-shell__sidebar {
      width: 264px;
      height: 100vh;
      min-height: 100vh;
      max-height: 100vh;
      flex-shrink: 0;
      align-self: flex-start;
      position: sticky;
      top: 0;
      display: flex;
      flex-direction: column;
      background-color: var(--tenant-sidebar-bg, var(--tenant-secondary, #0f172a));
      background-image:
        linear-gradient(
          180deg,
          rgba(15, 23, 42, var(--tenant-sidebar-overlay-opacity, 0.88)),
          rgba(15, 23, 42, var(--tenant-sidebar-overlay-opacity, 0.88))
        ),
        var(--tenant-sidebar-bg-image, none);
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
      color: var(--tenant-sidebar-text, #f8fafc);
      border-right: 1px solid color-mix(in srgb, var(--tenant-secondary, #0f172a) 82%, white);
      box-sizing: border-box;
      z-index: 30;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
    }

    .app-shell__sidebar::-webkit-scrollbar {
      width: 0;
      height: 0;
      display: none;
    }

    .app-shell__main {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    .app-shell__topbar {
      min-height: 64px;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 18px;
      box-sizing: border-box;
      border-bottom: 1px solid #dbe2ea;
      background-color: rgba(255, 255, 255, 0.9);
      background-image:
        linear-gradient(
          180deg,
          rgba(255, 255, 255, var(--tenant-topbar-overlay-opacity, 0.82)),
          rgba(255, 255, 255, var(--tenant-topbar-overlay-opacity, 0.82))
        ),
        var(--tenant-topbar-bg-image, none);
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
      backdrop-filter: blur(6px);
    }

    .app-shell__menu-toggle {
      width: 40px;
      height: 40px;
      display: none;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 4px;
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      background: #ffffff;
      padding: 0;
      cursor: pointer;
    }

    .app-shell__menu-toggle:focus-visible {
      outline: 2px solid var(--tenant-primary, #2563eb);
      outline-offset: 2px;
    }

    .app-shell__menu-toggle span {
      width: 16px;
      height: 2px;
      background: #1f2937;
      border-radius: 2px;
      display: block;
    }

    .app-shell__topbar-content {
      flex: 1;
      min-width: 0;
    }

    .app-shell__content {
      flex: 1;
      min-width: 0;
      padding: 20px;
      box-sizing: border-box;
      background-image:
        linear-gradient(
          180deg,
          rgba(248, 250, 252, var(--tenant-content-overlay-opacity, 0.93)),
          rgba(248, 250, 252, var(--tenant-content-overlay-opacity, 0.93))
        ),
        var(--tenant-content-bg-image, none);
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
    }

    @media (max-width: 960px) {
      .app-shell {
        min-height: 100dvh;
      }

      .app-shell__menu-toggle {
        display: inline-flex;
      }

      .app-shell__sidebar {
        position: fixed;
        top: 0;
        left: 0;
        width: min(320px, 86vw);
        height: 100vh;
        transform: translateX(-105%);
        transition: transform 0.24s ease;
        box-shadow: 0 16px 40px rgba(15, 23, 42, 0.35);
        max-height: 100dvh;
      }

      .app-shell__overlay {
        position: fixed;
        inset: 0;
        background: rgba(2, 6, 23, 0.55);
        backdrop-filter: blur(2px);
        z-index: 20;
        transition: opacity 0.2s ease;
      }

      .app-shell--mobile-open .app-shell__sidebar {
        transform: translateX(0);
      }

      .app-shell--mobile-open .app-shell__overlay {
        display: block;
        opacity: 1;
      }

      .app-shell__topbar {
        align-items: flex-start;
        padding: 12px;
      }

      .app-shell__content {
        padding: 12px;
      }
    }

    @media (max-width: 640px) {
      .app-shell__menu-toggle {
        width: 42px;
        height: 42px;
      }

      .app-shell__topbar {
        gap: 10px;
      }

      .app-shell__content {
        padding: 10px;
      }
    }
  `]
})
export class AppShellLayoutComponent implements OnDestroy {
  private static readonly EDGE_OPEN_ZONE_PX = 28;
  private static readonly SWIPE_TRIGGER_PX = 56;

  private readonly doc = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  protected isMobileSidebarOpen = false;
  private shellTouchStartX: number | null = null;
  private shellTouchStartY: number | null = null;
  private sidebarTouchStartX: number | null = null;
  private sidebarTouchStartY: number | null = null;

  protected toggleMobileSidebar(): void {
    this.setMobileSidebarOpen(!this.isMobileSidebarOpen);
  }

  protected closeMobileSidebar(): void {
    this.setMobileSidebarOpen(false);
  }

  protected onSidebarInteraction(event: MouseEvent): void {
    if (!this.isMobileSidebarOpen || !this.isMobileViewport()) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const shouldClose = !!target.closest('a[href], button, [data-app-shell-close]');
    if (shouldClose) {
      this.setMobileSidebarOpen(false);
    }
  }

  protected onShellTouchStart(event: TouchEvent): void {
    if (this.isMobileSidebarOpen || !this.isMobileViewport() || event.touches.length !== 1) {
      this.shellTouchStartX = null;
      this.shellTouchStartY = null;
      return;
    }

    const touch = event.touches[0];
    if (touch.clientX > AppShellLayoutComponent.EDGE_OPEN_ZONE_PX) {
      this.shellTouchStartX = null;
      this.shellTouchStartY = null;
      return;
    }

    this.shellTouchStartX = touch.clientX;
    this.shellTouchStartY = touch.clientY;
  }

  protected onShellTouchEnd(event: TouchEvent): void {
    if (
      this.isMobileSidebarOpen
      || !this.isMobileViewport()
      || this.shellTouchStartX === null
      || this.shellTouchStartY === null
      || event.changedTouches.length !== 1
    ) {
      this.shellTouchStartX = null;
      this.shellTouchStartY = null;
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.shellTouchStartX;
    const deltaY = touch.clientY - this.shellTouchStartY;
    this.shellTouchStartX = null;
    this.shellTouchStartY = null;

    if (
      deltaX >= AppShellLayoutComponent.SWIPE_TRIGGER_PX
      && Math.abs(deltaX) > Math.abs(deltaY) * 1.2
    ) {
      this.setMobileSidebarOpen(true);
    }
  }

  protected onSidebarTouchStart(event: TouchEvent): void {
    if (!this.isMobileSidebarOpen || !this.isMobileViewport() || event.touches.length !== 1) {
      this.sidebarTouchStartX = null;
      this.sidebarTouchStartY = null;
      return;
    }

    const touch = event.touches[0];
    this.sidebarTouchStartX = touch.clientX;
    this.sidebarTouchStartY = touch.clientY;
  }

  protected onSidebarTouchEnd(event: TouchEvent): void {
    if (
      !this.isMobileSidebarOpen
      || !this.isMobileViewport()
      || this.sidebarTouchStartX === null
      || this.sidebarTouchStartY === null
      || event.changedTouches.length !== 1
    ) {
      this.sidebarTouchStartX = null;
      this.sidebarTouchStartY = null;
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.sidebarTouchStartX;
    const deltaY = touch.clientY - this.sidebarTouchStartY;
    this.sidebarTouchStartX = null;
    this.sidebarTouchStartY = null;

    if (
      deltaX <= -AppShellLayoutComponent.SWIPE_TRIGGER_PX
      && Math.abs(deltaX) > Math.abs(deltaY) * 1.2
    ) {
      this.setMobileSidebarOpen(false);
    }
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (!this.isMobileViewport()) {
      this.setMobileSidebarOpen(false);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: Event): void {
    if (!this.isMobileSidebarOpen) {
      return;
    }

    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    this.setMobileSidebarOpen(false);
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  private setMobileSidebarOpen(next: boolean): void {
    if (this.isMobileSidebarOpen === next) {
      return;
    }

    this.isMobileSidebarOpen = next;

    if (this.isMobileSidebarOpen && this.isMobileViewport()) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  private isMobileViewport(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(max-width: 960px)').matches;
  }

  private lockBodyScroll(): void {
    this.renderer.setStyle(this.doc.body, 'overflow', 'hidden');
  }

  private unlockBodyScroll(): void {
    this.renderer.removeStyle(this.doc.body, 'overflow');
  }
}
